import type { IndustrialCaseDocument } from './industrialCaseDocument';

type Reference = IndustrialCaseDocument['references'][number];

/**
 * 본문에 삽입되는 인용 마커의 저장 형식.
 *
 * 마커는 참고 자료의 `id` 만 들고 있고 번호는 갖지 않는다.
 * 번호는 렌더링 시점에 참고 자료 목록 순서로 계산하므로,
 * 참고 자료를 재정렬해도 본문에 박힌 숫자가 어긋나지 않는다.
 */
export const CASE_CITATION_ELEMENT = 'sup';
export const CASE_CITATION_CLASS = 'case-citation';
export const CASE_CITATION_ID_ATTRIBUTE = 'data-case-citation';

export function buildCaseCitationHtml(referenceId: string) {
  return `<${CASE_CITATION_ELEMENT} class="${CASE_CITATION_CLASS}" ${CASE_CITATION_ID_ATTRIBUTE}="${referenceId}"></${CASE_CITATION_ELEMENT}>`;
}

/** 참고 자료 목록에서 `[n]` 번호를 계산한다 (1-base, 목록 순서). */
export function resolveCaseCitationNumber(
  references: Pick<Reference, 'id'>[],
  referenceId: string,
) {
  const index = references.findIndex(
    (reference) => reference.id === referenceId,
  );

  return index === -1 ? null : index + 1;
}

/** 본문 HTML 이 실제로 참조하는 참고 자료 id 목록 (등장 순서, 중복 제거). */
export function extractCitedReferenceIds(bodyHtml: string) {
  const pattern = new RegExp(
    `${CASE_CITATION_ID_ATTRIBUTE}=["']([^"']+)["']`,
    'g',
  );

  return [...new Set(Array.from(bodyHtml.matchAll(pattern), ([, id]) => id))];
}

/**
 * 본문 어디에서도 인용되지 않은 참고 자료.
 * 작성자에게 "등록만 하고 본문에 연결하지 않았다"를 알려주는 데 쓴다.
 */
export function findUncitedReferences<T extends Pick<Reference, 'id'>>(
  references: T[],
  bodyHtmls: string[],
) {
  const cited = new Set(bodyHtmls.flatMap(extractCitedReferenceIds));

  return references.filter((reference) => !cited.has(reference.id));
}

/**
 * 본문이 참조하지만 목록에 없는 인용 id.
 * 참고 자료를 지운 뒤 본문 마커가 남은 경우를 잡는다.
 */
export function findDanglingCitationIds(
  references: Pick<Reference, 'id'>[],
  bodyHtmls: string[],
) {
  const known = new Set(references.map((reference) => reference.id));

  return [...new Set(bodyHtmls.flatMap(extractCitedReferenceIds))].filter(
    (id) => !known.has(id),
  );
}

/** 본문에서 인용 마커를 찾는 패턴. CKEditor 가 내보내는 빈 `<sup>` 을 잡는다. */
const CITATION_MARKER_PATTERN = new RegExp(
  `<${CASE_CITATION_ELEMENT}\\b[^>]*\\b${CASE_CITATION_ID_ATTRIBUTE}=["']([^"']*)["'][^>]*>\\s*</${CASE_CITATION_ELEMENT}>`,
  'g',
);

export const CASE_REFERENCE_ANCHOR_PREFIX = 'case-reference';
export const CASE_CITATION_ANCHOR_PREFIX = 'case-citation';

/**
 * 본문의 인용 마커를 눌러서 이동할 수 있는 `[n]` 표시로 바꾼다.
 *
 * 번호와 링크는 저장된 데이터가 아니라 참고 자료 목록에서 계산한다.
 * 목록에 없는 id 는 조용히 지우지 않고 눈에 보이게 남겨, 작성자가
 * 삭제된 자료를 가리키는 인용을 발견할 수 있게 한다.
 */
export type CaseCitationMarker = {
  /** 목록 순서로 계산한 번호. 목록에 없는 자료면 null */
  number: number | null;
  reference?: Reference;
};

/**
 * 마커 기본 표현. 화면별 서식이 필요하면 `renderMarker` 로 갈아끼운다
 * (예: 미리보기는 공개 페이지의 인용 표시와 같은 마크업을 쓴다).
 */
function renderDefaultMarker({ number }: CaseCitationMarker) {
  if (number === null) {
    return `<sup class="${CASE_CITATION_CLASS} ${CASE_CITATION_CLASS}--missing">[?]</sup>`;
  }

  return [
    `<sup class="${CASE_CITATION_CLASS}" id="${CASE_CITATION_ANCHOR_PREFIX}-${number}">`,
    `<a href="#${CASE_REFERENCE_ANCHOR_PREFIX}-${number}">[${number}]</a>`,
    '</sup>',
  ].join('');
}

export function resolveCaseCitationsInHtml(
  bodyHtml: string,
  references: Reference[] | Pick<Reference, 'id'>[],
  renderMarker: (marker: CaseCitationMarker) => string = renderDefaultMarker,
) {
  return bodyHtml.replace(CITATION_MARKER_PATTERN, (_match, rawId: string) => {
    const number = resolveCaseCitationNumber(references, rawId);
    const reference = references.find((candidate) => candidate.id === rawId) as
      Reference | undefined;

    return renderMarker({ number, reference });
  });
}
